import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import logo from "./assets/logo.jpeg";

// Configure axios instance with full backend URL
const api = axios.create({
  baseURL: 'https://apigm.sriganapathymedical.shop/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

const Login = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Please enter email and password");
      return;
    }

    setError("");
    setInfoMessage("");
    setLoading(true);

    try {
      const response = await api.post("/login", { email, password });
      const data = response.data;

      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("loginType", "standard");
      setLoading(false);
      navigate("/dashboard");

    } catch (err) {
      console.error("Login error:", err);
      const errorMsg = err.response?.data?.error || "Server error. Please check your connection and try again.";
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setError("");
    setInfoMessage("Please contact the administrator to reset your password.");
  };

  // Responsive styles
  const styles = {
    container: {
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
      padding: isMobile ? "20px" : "0",
    },
    form: {
      background: "#1e1e2f",
      padding: isMobile ? "24px" : "35px",
      borderRadius: "12px",
      width: isMobile ? "100%" : "420px",
      maxWidth: "420px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
      color: "#fff",
      margin: isMobile ? "10px" : "0",
    },
    title: {
      textAlign: "center",
      marginBottom: "5px",
      marginTop: "10px",
      fontSize: isMobile ? "20px" : "24px",
      letterSpacing: isMobile ? "1px" : "2px",
    },
    logoContainer: {
      display: "flex",
      justifyContent: "center",
      marginBottom: "20px",
    },
    logo: {
      width: isMobile ? "80px" : "120px",
      height: "auto",
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
    },
    subtitle: {
      textAlign: "center",
      fontSize: isMobile ? "12px" : "14px",
      marginBottom: "20px",
      color: "#aaa",
      letterSpacing: isMobile ? "0.5px" : "1px",
    },
    inputGroup: {
      marginBottom: "18px",
      display: "flex",
      flexDirection: "column",
    },
    label: {
      fontSize: isMobile ? "13px" : "14px",
      marginBottom: "6px",
      fontWeight: "500",
    },
    input: {
      padding: isMobile ? "10px 12px" : "10px",
      borderRadius: "6px",
      border: "1px solid #444",
      backgroundColor: "#2a2a40",
      color: "#fff",
      outline: "none",
      fontSize: isMobile ? "14px" : "16px",
      width: "100%",
      boxSizing: "border-box",
      transition: "border-color 0.3s, box-shadow 0.3s",
    },
    passwordWrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      width: "100%",
    },
    passwordInput: {
      width: "100%",
      padding: isMobile ? "10px 12px" : "10px",
      paddingRight: "40px",
      borderRadius: "6px",
      border: "1px solid #444",
      backgroundColor: "#2a2a40",
      color: "#fff",
      outline: "none",
      fontSize: isMobile ? "14px" : "16px",
      boxSizing: "border-box",
      transition: "border-color 0.3s, box-shadow 0.3s",
    },
    eyeIcon: {
      position: "absolute",
      right: "12px",
      cursor: "pointer",
      color: "#aaa",
      fontSize: isMobile ? "16px" : "18px",
      padding: "4px",
      transition: "color 0.3s",
    },
    forgotWrapper: {
      textAlign: "right",
      marginBottom: "15px",
    },
    forgot: {
      fontSize: isMobile ? "12px" : "13px",
      color: "#10b981",
      cursor: "pointer",
      transition: "color 0.3s",
    },
    button: {
      width: "100%",
      padding: isMobile ? "12px" : "12px",
      backgroundColor: "#10b981",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontWeight: "bold",
      fontSize: isMobile ? "14px" : "16px",
      transition: "background-color 0.3s, transform 0.2s, box-shadow 0.3s",
      boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
    },
    error: {
      color: "#ff4d4d",
      fontSize: isMobile ? "13px" : "14px",
      marginBottom: "10px",
      padding: "8px 12px",
      backgroundColor: "rgba(255, 77, 77, 0.1)",
      borderRadius: "6px",
      border: "1px solid rgba(255, 77, 77, 0.2)",
    },
    info: {
      color: "#10b981",
      fontSize: isMobile ? "13px" : "14px",
      marginBottom: "10px",
      padding: "8px 12px",
      backgroundColor: "rgba(16, 185, 129, 0.1)",
      borderRadius: "6px",
      border: "1px solid rgba(16, 185, 129, 0.2)",
    },
    inputFocus: {
      borderColor: "#10b981",
      boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.2)",
    },
  };

  // Handle focus styles
  const handleInputFocus = (e) => {
    e.target.style.borderColor = "#10b981";
    e.target.style.boxShadow = "0 0 0 2px rgba(16, 185, 129, 0.2)";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#444";
    e.target.style.boxShadow = "none";
  };

  return (
    <div style={styles.container}>
      <form style={styles.form} onSubmit={handleSubmit}>
        <div style={styles.logoContainer}>
          <img src={logo} alt="GNANAPATHYMEDICALS Logo" style={styles.logo} />
        </div>
        <h2 className="brand-royal" style={styles.title}>
          {isMobile ? "GANAPATHY" : "GANAPATHYMEDICALS"}
        </h2>
        <p className="brand-royal-subtitle" style={styles.subtitle}>
          {isMobile ? "Pharmacy Management" : "Pharmacy Management System"}
        </p>

        {error && <p style={styles.error}>{error}</p>}
        {infoMessage && <p style={styles.info}>{infoMessage}</p>}

        <div style={styles.inputGroup}>
          <label style={styles.label}>Email</label>
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            style={styles.input}
            autoComplete="email"
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Password</label>
          <div style={styles.passwordWrapper}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              style={styles.passwordInput}
              autoComplete="current-password"
            />
            <span
              style={styles.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
              onMouseEnter={(e) => e.currentTarget.style.color = "#10b981"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#aaa"}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
        </div>

        <div style={styles.forgotWrapper}>
          <span
            style={styles.forgot}
            onClick={handleForgotPassword}
            onMouseEnter={(e) => e.currentTarget.style.color = "#34d399"}
            onMouseLeave={(e) => e.currentTarget.style.color = "#10b981"}
          >
            Forgot Password?
          </span>
        </div>

        <button
          type="submit"
          style={styles.button}
          disabled={loading}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = "#059669";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(16, 185, 129, 0.4)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#10b981";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 15px rgba(16, 185, 129, 0.3)";
          }}
        >
          {loading ? "Logging in..." : "Login"}
        </button>
      </form>

      <style>
        {`
          .brand-royal {
            font-family: 'Georgia', serif;
            font-weight: 700;
            background: linear-gradient(135deg, #f6d365, #fda085);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            text-shadow: none;
          }
          .brand-royal-subtitle {
            font-family: 'Georgia', serif;
            font-weight: 400;
            background: linear-gradient(135deg, #a8e063, #56ab2f);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            text-shadow: none;
          }
          @media (max-width: 480px) {
            .brand-royal {
              font-size: 18px !important;
            }
            .brand-royal-subtitle {
              font-size: 11px !important;
            }
          }
          @media (min-width: 481px) and (max-width: 768px) {
            .brand-royal {
              font-size: 20px !important;
            }
            .brand-royal-subtitle {
              font-size: 12px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Login;